import * as am4core from '@amcharts/amcharts4/core';
import { ForceDirectedSeries, ForceDirectedTree } from '@amcharts/amcharts4/plugins/forceDirected';
import Am4themesAnimated from '@amcharts/amcharts4/themes/animated';
import { makeStyles } from '@material-ui/core';
import { useParams } from '@reach/router';
import { clone } from 'ramda';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga';
import { useTranslation } from 'react-i18next';

import { useClassifierState } from '../../../../../../contexts/ClassifiersContext/ClassifiersContext';
import { ClassifiersTypes } from '../../../../../../contexts/ClassifiersContext/ClassifiersContext.types';
import { api } from '../../../../../../core/api';
import { mainTheme } from '../../../../../../core/config/mainTheme';
import {
  getActiveDataset,
  getActiveDbVersion,
  parseVersionedUid,
} from '../../../../../../core/utils/utils';
import { useAppGoogleReCaptcha } from '../../../../../../hooks/useAppGoogleReCaptcha';
import { useIsMount } from '../../../../../../hooks/useOnChange';
import { useSnackbarMessage } from '../../../../../../hooks/useSnackbarMessage';
import { useBenchmarkState } from '../../../../../Benchmark/Context/BenchmarkContext';
import { useCompaniesState } from '../../../../shared/CompanyContext/CompanyContext';
import { useErrorsDispatch } from '../../../../shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../../shared/ErrorContext/ErrorContext.types';
import {
  useConnectionsDispatch,
  useConnectionsState,
} from '../ConnectionsContext/ConnectionsContext';
import { ConnectionsContextActionTypes } from '../ConnectionsContext/ConnectionsContext.types';
import {
  exportMenu,
  MappedGraphNode,
  NodesColorEnum,
  NodesRadiustEnum,
} from './ConnectionsChart.type';
import {
  generateFormValues,
  generateNodes,
  nodesToggler,
  setCompanyToCenter,
  setExportingData,
  setHiddenNodes,
  setHistoricalStroke,
  setMappedNodes,
  setStrokeWidth,
  setTooltipLinks,
} from './ConnectionsChart.utils';

am4core.useTheme(Am4themesAnimated);

interface Props {}

const ConnectionsChart: React.FC<Props> = () => {
  const { form, companyId, connectionsCounts } = useConnectionsState();
  const dispatch = useConnectionsDispatch();
  const dispatchErrors = useErrorsDispatch();
  const { executeRecaptcha } = useAppGoogleReCaptcha();
  const chart = useRef(null);
  const classes = useStyles();
  const { t } = useTranslation();
  const { companyUid, dataset: datasetParam, dbVersion: dbVersionParam } = useParams<{
    companyUid?: string;
    dataset?: string;
    dbVersion?: string;
  }>();
  const [rawData, setRawData] = useState<MappedGraphNode[]>([]);
  const [filteredRawData, setFilteredRawData] = useState<MappedGraphNode[]>([]);
  const [visibleDataCount, setVisibleDataCount] = useState<number>(0);
  const [series, setSeries] = useState<ForceDirectedSeries>();
  const [loadingIndicator, setLoadingIndicator] = useState<am4core.Container>();
  const [mappedLinks] = useState<any>({});
  const [filteredMappedLinks, setFilteredMappedLinks] = useState<any>({});
  const isFirstMount = useIsMount();
  const textColor = mainTheme.palette.secondary.light;
  const colorWhite = mainTheme.palette.background.paper;
  const classifierRealtionType = useClassifierState(ClassifiersTypes.RelationType);
  const classifierOwnershipType = useClassifierState(ClassifiersTypes.OwnershipType);
  const classifierRegionCountry = useClassifierState(ClassifiersTypes.RegionAndCountry);
  const [hiddenClass, setHiddenClass] = useState<string>('');
  const [nodeHidden, setNodeHidden] = useState<boolean>(false);
  const [formManagementTypes, setFormManagementTypes] = useState<string[]>([]);

  const { dataset, dbVersion } = useCompaniesState();
  const { activeBenchmark } = useBenchmarkState();
  const datasetSource = datasetParam ?? dataset;
  const dbVersionSource = dbVersionParam ?? dbVersion;
  const activeDataset = getActiveDataset(datasetSource, activeBenchmark);
  const activeDbVersion = getActiveDbVersion(dbVersionSource, activeBenchmark);

  const plainCompanyUid = parseVersionedUid(companyUid ?? '');
  const plainCompanyId = parseVersionedUid(companyId ?? '');

  useEffect(() => {
    if (isFirstMount) {
      dispatch({
        type: ConnectionsContextActionTypes.SetCompanyId,
        payload: plainCompanyUid,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isFirstMount && series) {
      series.links.clear();
      form.historicalType.value ? setHiddenClass('hidden') : setHiddenClass('');
      const filteredData = nodesToggler(
        rawData,
        form,
        mappedLinks,
        setFilteredMappedLinks,
        setFormManagementTypes,
      );
      series.removeData(visibleDataCount);
      setVisibleDataCount(filteredData.length);
      setFilteredRawData(filteredData);
      series.addData(filteredData);
      setCompanyToCenter(series.data, plainCompanyId);
      dispatch({
        type: ConnectionsContextActionTypes.SetConnectionsCounts,
        payload: { total: connectionsCounts.total, displayed: filteredData.length },
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  useEffect(() => {
    setRawData([]);
    if (plainCompanyId && series) {
      const getNewConnections = async () => {
        if (loadingIndicator) {
          loadingIndicator.show();
        }

        let data;
        try {
          const recaptchaToken = await executeRecaptcha?.('search');
          data = await api.company.getCompanyConnections(
            plainCompanyId,
            recaptchaToken!,
            activeDataset,
            activeDbVersion,
          );
        } catch (error) {
          dispatchErrors({
            type: ErrorContextActionTypes.SetError,
            payload: { error },
          });
        }

        series.links.clear();
        series.removeData(visibleDataCount);
        const countryCodeToNameMap: Map<string, string> = new Map();
        classifierRegionCountry
          .filter((c) => c.id && c.name)
          .forEach((c) => countryCodeToNameMap.set(c.id!, c.name!));

        if (data) {
          const mappedNodes = setMappedNodes(data);
          generateNodes(data, mappedLinks, plainCompanyId, countryCodeToNameMap);
          const [nodeVisibilityTypes, linkVisibilityType] = setHiddenNodes(
            data,
            mappedNodes,
            mappedLinks,
          );

          if (nodeVisibilityTypes.hidden.length) {
            setNodeHidden(true);
          }
          const generatedFormValues = generateFormValues(
            nodeVisibilityTypes,
            linkVisibilityType,
            form,
          );

          dispatch({
            type: ConnectionsContextActionTypes.SetFormState,
            payload: generatedFormValues,
          });

          setRawData(data);
          form.historicalType.value ? setHiddenClass('hidden') : setHiddenClass('');
          const filteredData = nodesToggler(
            data,
            generatedFormValues,
            mappedLinks,
            setFilteredMappedLinks,
            setFormManagementTypes,
          );

          dispatch({
            type: ConnectionsContextActionTypes.SetConnectionsCounts,
            payload: { total: data.length, displayed: filteredData.length },
          });
          data.forEach((item: MappedGraphNode) => {
            if (item.value === NodesRadiustEnum.MAIN_COMPANY && item.id) {
              dispatch({
                type: ConnectionsContextActionTypes.PushToPath,
                payload: { id: plainCompanyId, name: item.name! },
              });
            }
          });

          setVisibleDataCount(filteredData.length);
          series.addData(filteredData);
          setFilteredRawData(filteredData);
          setCompanyToCenter(series.data, plainCompanyId);
          setNodeHidden(false);
        }
        if (loadingIndicator) {
          loadingIndicator.hide();
        }
      };
      getNewConnections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plainCompanyId, activeDataset]);

  useEffect(() => {
    if (series) {
      setTooltipLinks(
        classifierRealtionType,
        classifierOwnershipType,
        series,
        t,
        hiddenClass,
        filteredMappedLinks,
        formManagementTypes,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenClass, formManagementTypes]);

  useSnackbarMessage(nodeHidden, 'connections:snackbarNotAllConnectionsText', {
    variant: 'info',
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'right',
    },
  });

  useLayoutEffect(() => {
    const x = am4core.create('chartdiv', ForceDirectedTree);

    const indicator = x.tooltipContainer!.createChild(am4core.Container);
    indicator.background.fill = am4core.color('#fff');
    indicator.background.fillOpacity = 0.9;
    indicator.width = am4core.percent(100);
    indicator.height = am4core.percent(100);

    const indicatorLabel = indicator.createChild(am4core.Label);
    indicatorLabel.text = 'Loading...';
    indicatorLabel.align = 'center';
    indicatorLabel.valign = 'middle';
    indicatorLabel.fontSize = 40;
    indicatorLabel.dy = 50;
    indicator.hide(0);
    setLoadingIndicator(indicator);

    const series = x.series.push(new ForceDirectedSeries());

    // init export menu
    x.exporting.menu = new am4core.ExportMenu();
    x.exporting.menu.align = 'left';
    x.exporting.menu.verticalAlign = 'top';

    x.exporting.menu.items = [exportMenu];
    // init export menu data for download csv
    setExportingData(series);
    setSeries(series);

    // setRawData(filteredRawData);
    setVisibleDataCount(filteredRawData.length);

    series.data = setCompanyToCenter(clone(filteredRawData), plainCompanyId);

    series.fontSize = 12;
    series.minRadius = 15;
    series.maxRadius = 60;
    series.centerStrength = 0;
    series.manyBodyStrength = -20;

    // Set up data fields
    series.dataFields.id = 'id';
    series.dataFields.value = 'value';
    series.dataFields.name = 'name';
    series.dataFields.linkWith = 'linkWith';
    series.dataFields.children = 'children';
    series.dataFields.color = 'color';
    series.dataFields.fixed = 'fixed';

    // Add labels
    series.nodes.template.label.text = '{name} \n{countryLabel}';
    series.nodes.template.label.dy = 15;
    series.nodes.template.label.valign = 'bottom';
    series.nodes.template.label.fill = am4core.color(textColor);
    series.nodes.template.outerCircle.disabled = false;
    series.nodes.template.togglable = false;
    series.nodes.template.cursorOverStyle = am4core.MouseCursorStyle.pointer;
    series.nodes.template.propertyFields.x = 'x';
    series.nodes.template.propertyFields.y = 'y';
    series.nodes.template.circle.cursorOverStyle = am4core.MouseCursorStyle.pointer;

    series.links.template.propertyFields.fill = '#000';

    // set nodes not so mobility. 0-chaotic, 1-fully static
    series.events.on('inited', function () {
      series.animate(
        {
          property: 'velocityDecay',
          to: 0.9,
        },
        500,
      );
    });

    series.nodes.template.events.on('hit', (event) => {
      const node: any = event.target;
      if (
        node.dataItem.dataContext.companyUid &&
        node.dataItem.dataContext.companyUid !== plainCompanyId
      ) {
        ReactGA.event({ category: 'CONNECTIONS_CHART', action: 'COMPANY_NODE_CLICK' });
        dispatch({
          type: ConnectionsContextActionTypes.SetCompanyId,
          payload: node.dataItem.dataContext.companyUid,
        });
      }
    });

    const hoverState = series.nodes.template.states.create('highlight');
    hoverState.properties.scale = 1.3;

    series.nodes.template.events.on('over', function (ev) {
      const node: any = ev.target;
      if (
        node.dataItem.dataContext.companyUid &&
        node.dataItem.dataContext.companyUid !== plainCompanyId
      ) {
        ev.target.setState('highlight');
      }
    });

    series.nodes.template.events.on('out', function (ev) {
      ev.target.setState('default');
    });

    // Add icons
    // Configure icons
    const icon = series.nodes.template.createChild(am4core.Sprite);
    icon.propertyFields.path = 'path';
    icon.strokeWidth = 0;
    icon.fill = am4core.color(colorWhite);
    icon.horizontalCenter = 'middle';
    icon.verticalCenter = 'middle';
    icon.cursorOverStyle = am4core.MouseCursorStyle.pointer;

    // link
    setStrokeWidth(series, filteredMappedLinks);
    setHistoricalStroke(series, filteredMappedLinks);
    setTooltipLinks(
      classifierRealtionType,
      classifierOwnershipType,
      series,
      t,
      hiddenClass,
      filteredMappedLinks,
      formManagementTypes,
    );

    series.links.template.distance = 3;
    series.links.template.interactionsEnabled = true;
    series.links.template.fill = am4core.color(NodesColorEnum.TOOLTIP_BG);
    series.links.template.strokeWidth = 0;

    // set color for links
    series.links.template.adapter.add('stroke', () => {
      return am4core.color(NodesColorEnum.COMPANY);
    });

    // Zoom
    // x.zoomable = true;
    // x.maxZoomLevel = 4;

    // x.zoomOutButton.background.cornerRadius(0, 0, 0, 0);
    // x.zoomOutButton.background.fill = am4core.color(NodesColorEnum.PERSON);
    // x.zoomOutButton.showOnInit = true;
    // x.zoomOutButton.icon.stroke = am4core.color(colorWhite);
    // x.zoomOutButton.icon.strokeWidth = 2;
    // x.zoomOutButton.interactionsEnabled = true;
    // x.zoomOutButton.cursorOverStyle = am4core.MouseCursorStyle.pointer;

    const hoverStateButton = x.zoomOutButton.background.states.create('hover');
    hoverStateButton.properties.fill = am4core.color(NodesColorEnum.BRANCH);
    hoverStateButton.properties.stroke = am4core.color(colorWhite);

    const downStateButton = x.zoomOutButton.background.states.create('down');
    downStateButton.properties.fill = am4core.color(NodesColorEnum.COMPANY);

    chart.current = x as any;

    return () => {
      x.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRawData, filteredMappedLinks]);

  return <div id="chartdiv" className={classes.chartBox}></div>;
};

const useStyles = makeStyles((theme) => ({
  chartBox: {
    flexGrow: 1,
    width: '100%',
    height: '100%',
    background: theme.palette.background.paper,
    boxShadow: '0 0 5px 0 rgba(0,0,0,0.5)',
    '& p': {
      margin: 0,
    },
    '& .hidden': {
      display: 'none',
    },
  },
}));

export default ConnectionsChart;
